import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Eye, Pencil, Share2, X } from "lucide-react"
import ItemCover from "components/ItemCover"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { TableCell, TableRow } from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { ProductStatusBadge, type ProductStatusVariant } from "@/components/ProductStatusBadge"
import { type Product } from "types"
import { type UnifiedGlassesItem, isProductComplete } from "./glassesRowModel"

export interface GlassesRowProps {
  item: UnifiedGlassesItem
  selected: boolean
  onToggleSelect: (id: string) => void
  onViewDemo: (product: Product) => void
  onEditProduct: (productId: string) => void
  onOpenDraft: (draftId: string) => void
  onCancelDraft: (draftId: string) => void
  onGenerateQrCode: (product: Product) => void
  /** Must return the mutation promise so the switch stays disabled in flight. */
  toggleVisibility: (product: Product) => Promise<unknown>
  viewDemoPending?: boolean
}

const statusBadgeVariant: Record<UnifiedGlassesItem["status"], ProductStatusVariant> = {
  ready: "ready",
  "need-setup": "needSetup",
  processing: "processing",
}

const actionButtonClassName = "h-8 gap-1 px-1.5 text-foreground/70 hover:text-foreground"

// Memoized for the same reason as MakeupRow: an optimistic toggle replaces the
// whole list array and would otherwise re-render every row. Handlers passed in
// must be identity-stable.
const GlassesRowComponent: React.FC<GlassesRowProps> = ({
  item,
  selected,
  onToggleSelect,
  onViewDemo,
  onEditProduct,
  onGenerateQrCode,
  onOpenDraft,
  onCancelDraft,
  toggleVisibility,
  viewDemoPending = false,
}) => {
  const { t } = useTranslation()
  const [visibilityLoading, setVisibilityLoading] = useState(false)

  const cover = item.kind === "draft" ? item.draft.cover : item.product.cover
  const name = item.kind === "draft" ? item.draft.name : item.product.name

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement
    if (
      target.closest("button") ||
      target.closest('[role="switch"]') ||
      target.closest('[role="checkbox"]') ||
      target.closest("label")
    )
      return
    onToggleSelect(item.id)
  }

  const handleToggleVisibility = async () => {
    if (item.kind !== "product" || visibilityLoading) return
    setVisibilityLoading(true)
    try {
      await toggleVisibility(item.product)
    } catch {
      // The mutation's onError already alerts and rolls back the cache.
    } finally {
      setVisibilityLoading(false)
    }
  }

  const canToggleVisibility =
    item.kind === "product" && item.status === "ready" && isProductComplete(item.product)
  const canGenerateQrCode = item.kind === "product" && item.status === "ready"

  const isVisible = item.kind === "product" ? Boolean(item.product.visible) : false

  return (
    <TableRow
      data-testid={item.kind === "draft" ? "glasses-draft-row" : "glasses-product-row"}
      data-item-id={item.id}
      data-state={selected ? "selected" : undefined}
      className="group cursor-pointer data-[state=selected]:bg-vto-accent-soft"
      onClick={handleRowClick}
    >
      <TableCell className="pl-3">
        <Checkbox
          data-testid="glasses-item-select"
          aria-label={`Select ${name}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(item.id)}
          className={
            selected
              ? ""
              : "opacity-0 transition-opacity group-hover:opacity-60 group-hover:hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
          }
        />
      </TableCell>
      <TableCell className="min-w-0">
        <div className="flex min-w-0 items-center gap-3">
          <div className="shrink-0 overflow-hidden rounded-md bg-muted/40">
            <ItemCover
              cover={cover && cover.length ? cover : "/none-image.svg"}
              name={name}
              size={40}
            />
          </div>
          <span className="block min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <ProductStatusBadge variant={statusBadgeVariant[item.status]}>
          {t(
            item.status === "ready"
              ? "glasses.status.ready"
              : item.status === "need-setup"
              ? "glasses.status.needSetup"
              : "glasses.status.processing"
          )}
        </ProductStatusBadge>
      </TableCell>
      <TableCell className="whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
        {item.kind === "product" ? (
          <div className="flex items-center gap-2">
            <Switch
              data-testid="glasses-product-visibility"
              className="cursor-pointer"
              disabled={visibilityLoading || !canToggleVisibility}
              checked={isVisible}
              onCheckedChange={() => void handleToggleVisibility()}
            />
            <span className="text-sm text-foreground/70">
              {t(isVisible ? "glasses.tryOnButton.on" : "glasses.tryOnButton.off")}
            </span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
        <div className="flex justify-end gap-1">
          {item.kind === "product" && item.status === "ready" && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      data-testid="glasses-product-view-demo"
                      variant="ghost"
                      size="sm"
                      className={actionButtonClassName}
                      disabled={!isVisible || viewDemoPending}
                      aria-disabled={!isVisible || viewDemoPending}
                      aria-busy={viewDemoPending}
                      onClick={() => onViewDemo(item.product)}
                    >
                      <Eye className="size-4" />
                      {t("glasses.action.viewDemo")}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!isVisible ? (
                  <TooltipContent>{t("glasses.action.viewDemoDisabledHint")}</TooltipContent>
                ) : null}
              </Tooltip>
            </TooltipProvider>
          )}
          {canGenerateQrCode && item.kind === "product" && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="glasses-product-share"
                    aria-label={t("glasses.qrCode.actionLabel")}
                    variant="ghost"
                    size="sm"
                    className={actionButtonClassName}
                    onClick={() => onGenerateQrCode(item.product)}
                  >
                    <Share2 className="size-4" />
                    {t("glasses.action.share")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("glasses.qrCode.tooltip")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {item.status !== "processing" && (
            <Button
              data-testid={item.kind === "draft" ? "glasses-draft-edit" : "glasses-product-edit"}
              variant="ghost"
              size="sm"
              className={actionButtonClassName}
              onClick={() =>
                item.kind === "draft"
                  ? onOpenDraft(item.draft.draftId)
                  : onEditProduct(item.product.id)
              }
            >
              <Pencil className="size-4" />
              {t("glasses.action.edit")}
            </Button>
          )}
          {item.kind === "draft" && item.status === "processing" && (
            <Button
              data-testid="glasses-draft-cancel"
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onCancelDraft(item.draft.draftId)}
            >
              <X className="size-4" />
              {t("glasses.action.cancel")}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export const GlassesRow = React.memo(GlassesRowComponent)
